import {useEffect, useState} from 'react';

type UseLoadScriptProps = {
  src: string;
  id: string;
  enabled?: boolean;
};

type LoadScriptState = {
  isLoading: boolean;
  isLoaded: boolean;
  error: Error | null;
};

export const useLoadScript = ({
  src,
  id,
  enabled = true,
}: UseLoadScriptProps): LoadScriptState => {
  const [state, setState] = useState<LoadScriptState>({
    isLoading: false,
    isLoaded: false,
    error: null,
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const existingScript = document.getElementById(id);
    if (existingScript) {
      setState({isLoading: false, isLoaded: true, error: null});
      return;
    }

    setState({isLoading: true, isLoaded: false, error: null});

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;

    const handleLoad = () => {
      setState({isLoading: false, isLoaded: true, error: null});
    };

    const handleError = () => {
      const error = new Error(`Failed to load script: ${src}`);
      setState({isLoading: false, isLoaded: false, error});
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, [src, id, enabled]);

  return state;
};
